// The app's world catalog. The engine ships a few dev/test worlds (the tulip
// render-path comparisons, the calibration grid) that must never reach a
// user-facing surface — library, pads, pickers, market, or the Director pool.
// By-id lookups (playlist names, an explicit pin) stay on Engine.WORLDS so
// old persisted references still resolve.

import type * as EngineT from '@syqel/engine';
import { Engine } from '@syqel/ota-client';

const EXCLUDED = new Set(['tulip-pipeline', 'tulip-hybrid', 'tulip-splat', 'calibration-grid']);

/** Engine.WORLDS minus dev/test worlds — read at call time (OTA swaps the namespace). */
export function appWorlds(): EngineT.World[] {
  return Engine.WORLDS.filter((w) => !EXCLUDED.has(w.id));
}

/** Every tier plays the whole catalog now — free is watermarked, paid removes
 *  the watermark (the ONLY gate). Kept as a function because journey / pads /
 *  library / hotkeys still filter through it, but it no longer locks anything. */
export function worldUnlocked(_id: string): boolean {
  return true;
}

/** The 2D source-field as a first-class world. Driven by frame.sources
 *  (detectSources), it rotates + cross-fades like any world: mode:'field'
 *  dispatches to the engine's SpatialFieldMode through otaModeFactory, and
 *  fieldId selects the generative preset. */
const SOURCE_FIELD: EngineT.World = {
  id: 'source-field',
  name: 'Source Field',
  archetype: 'mainstream',
  mode: 'field',
  fieldId: 'signature',
  palette: { hueBase: 285, hueRange: 200, saturation: 84, lightness: 60, bg: { r: 6, g: 7, b: 14 } },
  motion: { travelBase: 1.0, travelEnergy: 2.2, scatter: 1.0, starDensity: 0.9 },
};

/** Register app-owned worlds into the (possibly OTA-swapped) engine catalogue.
 *  Call AFTER OTA boot so the entry lands in the LIVE namespace; idempotent by
 *  id (registerWorld replaces on re-register). Read at call time — never at
 *  module scope — so it targets the swapped Engine. */
export function registerAppWorlds(): void {
  Engine.registerWorld(SOURCE_FIELD);
}

// TODO(marketplace): the store screen. @syqel/ota-client's getCatalogue() now
// returns the FULL store catalogue — including not-yet-owned worlds, browsable
// offline — and worldAllowed(id) is the ownership gate (allow-all until the
// platform flips it server-side; every world is free today). When the
// marketplace ships its UI wave, the browse/buy surface belongs in
// src/library/LibrarySection.tsx as a sibling tab of the library ("Market"),
// mapping getCatalogue() entries → purchase via the existing rails (MAS:
// StoreKit IAP paywall branch; else Stripe checkout). No UI this wave.
